import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Cake, Gift, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const weekdayLabels = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const monthNames = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// Parses using local date components so YYYY-MM-DD strings (parsed as UTC
// midnight by `new Date()`) don't shift a day earlier in negative UTC offsets.
const parseBirthDate = (value) => {
  if (typeof value === 'string') {
    if (value.includes('/')) {
      const [day, month, year] = value.split('/');
      return new Date(year, month - 1, day);
    }
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    return new Date(value);
  }
  return new Date(value);
};

const today = new Date();
const yearOptions = Array.from({ length: 106 }, (_, i) => today.getFullYear() + 5 - i);

const BirthdaysPage = () => {
  const [employeesWithBirthdate, setEmployeesWithBirthdate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState(today.getDate());

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // Rota enxuta (só ativos, só id/nome/foto/data_nascimento) — evita
      // buscar o cadastro completo de todos os funcionários só pra montar o calendário.
      const response = await axios.get(`${API}/employees/aniversariantes`);
      const employees = response.data;

      const parsed = employees
        .filter((employee) => !!employee.data_nascimento)
        .map((employee) => {
          let birthDate;
          try {
            birthDate = parseBirthDate(employee.data_nascimento);
          } catch (e) {
            return null;
          }
          if (Number.isNaN(birthDate.getTime())) return null;
          return {
            ...employee,
            birthday_date: birthDate
          };
        })
        .filter(Boolean);

      setEmployeesWithBirthdate(parsed);
    } catch (error) {
      console.error('Erro ao carregar aniversários:', error);
      setEmployeesWithBirthdate([]);
    } finally {
      setLoading(false);
    }
  };

  const birthdayEmployees = useMemo(() => {
    return employeesWithBirthdate
      .filter((employee) => employee.birthday_date.getMonth() + 1 === selectedMonth)
      .map((employee) => ({
        ...employee,
        age: selectedYear - employee.birthday_date.getFullYear()
      }))
      .sort((a, b) => a.birthday_date.getDate() - b.birthday_date.getDate());
  }, [employeesWithBirthdate, selectedMonth, selectedYear]);

  const isCurrentMonth = selectedMonth === today.getMonth() + 1 && selectedYear === today.getFullYear();
  const isSelectedDayToday = isCurrentMonth && selectedDay === today.getDate();
  const selectedDayBirthdays = selectedDay
    ? birthdayEmployees.filter((employee) => employee.birthday_date.getDate() === selectedDay)
    : [];
  const selectedDayLabel = selectedDay
    ? new Date(selectedYear, selectedMonth - 1, selectedDay).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })
    : '';

  const exportBirthdaysCSV = () => {
    if (birthdayEmployees.length === 0) return;

    const headers = ['Nome', 'Data de Nascimento', 'Idade'];
    const rows = birthdayEmployees.map((employee) => [
      employee.nome,
      employee.birthday_date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      employee.age
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `aniversariantes_${monthNames[selectedMonth - 1]}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const birthdayDaysInMonth = new Set(birthdayEmployees.map((employee) => employee.birthday_date.getDate()));
  const calendarYear = selectedYear;
  const daysInCalendarMonth = new Date(calendarYear, selectedMonth, 0).getDate();
  const firstWeekdayOfMonth = new Date(calendarYear, selectedMonth - 1, 1).getDay();
  const calendarCells = [
    ...Array(firstWeekdayOfMonth).fill(null),
    ...Array.from({ length: daysInCalendarMonth }, (_, i) => i + 1)
  ];

  const monthLabel = `${monthNames[selectedMonth - 1]} de ${calendarYear}`;

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const renderEmployeeRow = (employee, variant) => (
    <div
      key={employee.id}
      className={`flex items-center space-x-3 p-3 rounded-lg border ${
        variant === 'today' ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-200'
      }`}
    >
      <div className={`relative w-12 h-12 rounded-full overflow-hidden ${variant === 'today' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
        {employee.foto ? (
          <img src={employee.foto} alt={employee.nome} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gift className={`h-6 w-6 ${variant === 'today' ? 'text-yellow-600' : 'text-blue-600'}`} />
          </div>
        )}
      </div>
      <div className="flex-1">
        <p className="font-medium text-slate-800">{employee.nome}</p>
        <p className={`text-sm ${variant === 'today' ? 'text-yellow-700' : 'text-blue-600'}`}>
          {variant === 'today' && isSelectedDayToday
            ? `Hoje - ${employee.age} anos`
            : `${employee.birthday_date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })} - ${employee.age} anos`}
        </p>
      </div>
      <Cake className={`h-5 w-5 ${variant === 'today' ? 'text-yellow-500' : 'text-blue-500'}`} />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center space-x-2">
            <Cake className="h-7 w-7 text-blue-600" />
            <span>Aniversários</span>
          </h1>
          <p className="text-slate-600 mt-1">{monthLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousMonth} aria-label="Mês anterior">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="h-9 rounded-md border border-input px-3 text-sm shadow-sm bg-background"
          >
            {monthNames.map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
          <Button variant="outline" size="icon" onClick={goToNextMonth} aria-label="Próximo mês">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="h-9 rounded-md border border-input px-3 text-sm shadow-sm bg-background"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          {!isCurrentMonth && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedMonth(today.getMonth() + 1);
                setSelectedYear(today.getFullYear());
                setSelectedDay(today.getDate());
              }}
            >
              Hoje
            </Button>
          )}
          <Button variant="outline" onClick={exportBirthdaysCSV} disabled={birthdayEmployees.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mini Calendar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-blue-700">Calendário do Mês</CardTitle>
            <CardDescription>Dias com aniversariantes destacados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {weekdayLabels.map((label, index) => (
                <div key={index} className="text-center text-xs font-medium text-slate-400">
                  {label}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarCells.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} />;
                }
                const hasBirthday = birthdayDaysInMonth.has(day);
                const isToday = isCurrentMonth && day === today.getDate();
                const isSelected = day === selectedDay;
                return (
                  <button
                    type="button"
                    key={day}
                    onClick={() => setSelectedDay(day)}
                    className={`aspect-square flex items-center justify-center text-sm rounded-full cursor-pointer transition-colors ${
                      hasBirthday ? 'bg-blue-500 text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'
                    } ${isToday ? 'ring-2 ring-offset-1 ring-yellow-400' : ''} ${
                      isSelected ? 'ring-2 ring-offset-1 ring-blue-700' : ''
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center space-x-4 mt-3 pt-2 border-t border-slate-100 text-xs text-slate-500">
              <span className="flex items-center space-x-1">
                <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />
                <span>Aniversário</span>
              </span>
              <span className="flex items-center space-x-1">
                <span className="w-3 h-3 rounded-full ring-2 ring-yellow-400 inline-block" />
                <span>Hoje</span>
              </span>
              <span className="flex items-center space-x-1">
                <span className="w-3 h-3 rounded-full ring-2 ring-blue-700 inline-block" />
                <span>Selecionado</span>
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Today's Birthdays */}
        <Card className="border-l-4 border-l-yellow-400">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-yellow-700">
              <Gift className="h-5 w-5" />
              <span>Aniversariantes do Dia</span>
            </CardTitle>
            <CardDescription>
              {isSelectedDayToday ? `Hoje, ${selectedDayLabel}` : selectedDayLabel}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedDayBirthdays.length > 0 ? (
              <div className="space-y-3">
                {selectedDayBirthdays.map((employee) => renderEmployeeRow(employee, 'today'))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Gift className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nenhum aniversário em {selectedDayLabel}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Full Month List */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-blue-700">
            <Cake className="h-5 w-5" />
            <span>Aniversariantes do Mês</span>
          </CardTitle>
          <CardDescription>
            {birthdayEmployees.length} funcionário(s) fazem aniversário em {monthLabel}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {birthdayEmployees.length > 0 ? (
            <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
              {birthdayEmployees.map((employee) => renderEmployeeRow(employee, 'month'))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Cake className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Nenhum aniversário neste mês</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BirthdaysPage;
